"use client";

import { motion, useReducedMotion } from "motion/react";
import { Children, ReactNode } from "react";

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

interface RevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  y?: number;
}

/** Fade + slide up when scrolled into view. */
export function Reveal({ children, className, delay = 0, y = 24 }: RevealProps) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: "some" }}
      transition={{ duration: 0.5, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

interface StaggerProps {
  children: ReactNode;
  className?: string;
  gap?: number;
}

/**
 * Reveals its <StaggerItem> children one after another when scrolled into view.
 *
 * Grids that fetch their data mount empty, so we key the container on whether it
 * has children — when the data arrives it remounts and the scroll observer
 * re-initialises with the cards present (otherwise it would fire once on the
 * empty grid and leave the late-arriving cards stuck hidden).
 */
export function Stagger({ children, className, gap = 0.08 }: StaggerProps) {
  const hasChildren = Children.count(children) > 0;
  return (
    <motion.div
      key={hasChildren ? "filled" : "empty"}
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: "some" }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: gap } } }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
  y = 20,
}: {
  children: ReactNode;
  className?: string;
  y?: number;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={{
        hidden: reduce ? { opacity: 0 } : { opacity: 0, y },
        show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  );
}
